import type { BadgeDefinition } from '@/hooks/useBadgeDefinitions';
import type { BadgeAward } from '@/hooks/useBadgeAwards';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, Award, Users, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BadgeCardProps {
  badge: BadgeDefinition;
  awards?: BadgeAward[];
  onAward?: (d: string) => void;
  compact?: boolean;
}

export function BadgeCard({ badge, awards = [], onAward, compact = false }: BadgeCardProps) {
  const totalAwardees = new Set(awards.flatMap((a) => a.awardees)).size;
  const awardCount = awards.length;

  return (
    <Card
      className={cn(
        'border-border/60 bg-card/70 hover:bg-card/90 transition-colors overflow-hidden',
        compact && 'text-sm',
      )}
    >
      <CardContent className={cn('p-4', compact && 'p-3')}>
        <div className="flex items-start gap-3">
          {/* Badge image */}
          {(badge.thumbs[0]?.url ?? badge.image) ? (
            <img
              src={badge.thumbs[0]?.url ?? badge.image}
              alt={badge.name ?? badge.d}
              className={cn(
                'rounded-xl object-cover border border-border/40 shrink-0',
                compact ? 'h-12 w-12' : 'h-16 w-16',
              )}
              onError={(e) => {
                const el = e.target as HTMLImageElement;
                el.style.display = 'none';
                el.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div
            className={cn(
              'rounded-xl bg-primary/10 border border-border/40 flex items-center justify-center shrink-0',
              compact ? 'h-12 w-12' : 'h-16 w-16',
              (badge.thumbs[0]?.url ?? badge.image) ? 'hidden' : '',
            )}
          >
            <Trophy className={cn('text-primary', compact ? 'h-5 w-5' : 'h-7 w-7')} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className={cn('font-semibold truncate', compact ? 'text-sm' : 'text-base')}>
                  {badge.name ?? badge.d}
                </p>
                <p className="text-xs text-muted-foreground font-mono">{badge.d}</p>
              </div>
            </div>

            {badge.description && !compact && (
              <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">
                {badge.description}
              </p>
            )}

            {/* Stats row */}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Award className="h-3 w-3" />
                {awardCount} award{awardCount !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {totalAwardees} recipient{totalAwardees !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {!compact && onAward && (
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="flex-1"
              onClick={() => onAward(badge.d)}
            >
              <Award className="mr-1.5 h-3.5 w-3.5" />
              Award this badge
            </Button>
            {badge.image && (
              <Button
                size="sm"
                variant="ghost"
                className="px-2.5"
                asChild
              >
                <a href={badge.image} target="_blank" rel="noopener noreferrer" aria-label="View full image">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
